import React, { useState } from 'react'

import { NavLink as RouterNavLink, NavLinkProps, useParams } from 'react-router-dom'
import {
  CardContent,
  Chip,
  Grid,
  List,
  ListItem,
  Paper,
  Typography,
} from '@material-ui/core'
import { Add as AddIcon } from '@material-ui/icons'

import { useQueryPaginator } from 'src/components/Table'
import { AddAttachmentsDialog } from 'src/components/Dialog/AddAttachmentsDialog'
import { FreightOrderDetailTable } from 'src/components/Table/detail/FreightOrder'
import { FreightOrderDialog } from 'src/modules/supply_chain/components/freight/OrderDialog'
import { FreightShipmentDialog } from 'src/modules/supply_chain/components/freight/ShipmentDialog'
import { FreightOrderUnitListTable } from 'src/components/Table/list/FreightOrderUnit'
import { FreightOrderBarcodedUnitListTable } from 'src/components/Table/list/FreightOrderBarcodedUnit'
import { FreightOrderPalletListTable } from 'src/components/Table/list/FreightOrderPallet'
import { FreightShipmentListTable } from 'src/components/Table/list/FreightShipment'
import { EventListTable } from 'src/components/Table/list/Event'
import { GenericPreview, PaginatedFilePreviewGallery } from 'src/components/FilePreview'
import { Workflow } from 'src/modules/supply_chain/components/Workflow'

import { skuit } from 'src/services/skuit'
import { useQuery, UseQueryResult } from 'react-query'

import { DetailPage } from 'src/components/Pages/DetailPage'
import { DetailTab } from 'src/components/Pages/Tab'
import { Facility, FreightOrder, Tag } from 'src/api/models'

const NavLink = React.forwardRef((props: NavLinkProps, ref: React.Ref<HTMLAnchorElement>) => (
  <RouterNavLink innerRef={ref} {...props} />
))
NavLink.displayName = 'NavLink'

interface PathProps {
  guid: string
}

interface InventoryTabProps {
  order: UseQueryResult<FreightOrder>
}

const InventoryTab = (props: InventoryTabProps) => {
  const { order } = props
  const { guid } = useParams<PathProps>()

  const shipmentsPaginator = useQueryPaginator(
    'order-shipment-list',
    skuit.sc.freight.shipments.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )
  const unitsPaginator = useQueryPaginator(
    'order-units-list',
    skuit.sc.freight.orders.units.list,
    { params: { page_size: 5, orders: guid, ordering: 'status' },
  })
  const barcodedPaginator = useQueryPaginator(
    'order-barcoded-units',
    skuit.sc.freight.orders.barcodedUnits.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )
  const palletsPaginator = useQueryPaginator(
    'order-pallets',
    skuit.sc.freight.orders.pallets.list,
    {
      params: { page_size: 5, orders: guid },
    }
  )

  return (
    <DetailTab>
      <Paper>
        <CardContent>
          <Typography variant="h5" component="h3">
            {'Facilities'}
          </Typography>
          {order.data?.facilities && order.data?.facilities?.length > 0 ? (
            <List>
              {order.data?.facilities?.map((item: Facility, index: number) => (
                <ListItem
                  key={item.guid}
                  component="a"
                  href={`warehouse/facilities/${item.guid}`}
                >
                  {item.name}{' '}
                  {order.data.facilities && index !== order.data.facilities.length - 1 && ','}
                </ListItem>
              ))}{' '}
            </List>
          ) : (
            <Grid item container justify="center" xs={10}>
              <span>Nothing here yet.</span>
            </Grid>
          )}
        </CardContent>
      </Paper>
      <FreightShipmentListTable
        paginator={shipmentsPaginator}
        fields={['trackingNumber', 'consignee', 'facility', 'carrier', 'status']}
      />
      <FreightOrderUnitListTable
        paginator={unitsPaginator}
        title="Units"
        fields={['product', 'sku', 'container', 'batch', 'numUnits', 'status']}
      />
      <FreightOrderBarcodedUnitListTable
        paginator={barcodedPaginator}
        fields={['unit', 'product']}
      />
      <FreightOrderPalletListTable
        paginator={palletsPaginator}
        fields={['pallet', 'client', 'clientID', 'skus', 'path']}
      />
    </DetailTab>
  )
}

const AttachmentsTab = () => {
  const { guid } = useParams<PathProps>()
  const [addAttachmentDialogOpen, setAddAttachmentDialogOpen] = useState(false)

  const attachmentsPaginator = useQueryPaginator(
    'attachment-list',
    () => skuit.sc.freight.orders.attachments.list(guid),
    {
      params: { page_size: 4, orders: guid },
    }
  )

  return (
    <DetailTab>
      {addAttachmentDialogOpen && (
        <AddAttachmentsDialog
          onSuccess={() => attachmentsPaginator.refresh()}
          addAttachment={(config) => skuit.sc.freight.orders.attachments.create(guid, config?.data, config)}
          open={addAttachmentDialogOpen}
          handleClose={() => {
            setAddAttachmentDialogOpen(false)
          }}
        />
      )}
      <PaginatedFilePreviewGallery
        title="Attachments"
        paginator={attachmentsPaginator}
        getIdent={(item) => item.guid}
        onAdd={() => {
          setAddAttachmentDialogOpen(true)
        }}
        renderItem={(item) => {
          return (
            <>
              <GenericPreview url={item.file} />
              <div>
                {item.tags.map((tag: Tag) => (
                  <Chip key={tag.slug} label={tag.name} />
                ))}
              </div>
            </>
          )
        }}
      />
    </DetailTab>
  )
}

const EventsTab = () => {
  const { guid } = useParams<PathProps>()

  const eventsPaginator = useQueryPaginator('event-list', skuit.sc.events.list, {
    params: { page_size: 5, references: guid },
  })

  return (
    <DetailTab>
      <EventListTable
        paginator={eventsPaginator}
        title="Freight Order Events"
        fields={['type', 'data', 'by', 'on']}
      />
    </DetailTab>
  )
}

export const OrderPage = () => {
  const { guid } = useParams<PathProps>()
  const [createOpen, setCreateOpen] = useState(false)
  const [shipmentCreateOpen, setShipmentCreateOpen] = useState(false)

  const order = useQuery(['order-details', guid], () => skuit.sc.freight.orders.retrieve(guid))
  const customFieldValuePaginator = useQueryPaginator(
    'custom-field-values',
    skuit.sc.freight.orders.customFieldValues.list,
    { params: { orders: guid } }
  )
  const customFieldValues: Record<string, string> = {}
  for (const field of customFieldValuePaginator.data.results) {
    customFieldValues[field.name] = field.value
  }

  const refresh = () => {
    order.refetch()
  }

  return (
    <>
      {createOpen && (
        <FreightOrderDialog
          open={createOpen}
          handleClose={() => {
            setCreateOpen(false)
          }}
        />
      )}
      {shipmentCreateOpen && (
        <FreightShipmentDialog
          open={shipmentCreateOpen}
          handleClose={() => {
            setShipmentCreateOpen(false)
          }}
          order={order.data}
        />
      )}
      <DetailPage
        obj={order}
        title="Freight Order"
        refresh={refresh}
        breadcrumbs={[
          { title: 'Dashboard', url: '/' },
          { title: 'Freight Orders', url: '/freight/orders' },
        ]}
        detailComponent={
          <FreightOrderDetailTable
            obj={order}
            refresh={refresh}
            customFields={customFieldValues}
          />
        }
        secondaryComponent={
          <Workflow
            title="Order Workflow"
            loading={order.isFetching}
            workflow={order.data?.workflow || []}
            status={order.data?.workflowStatus || ''}
            actions={order.data?.getWorkflowActions(() => order.refetch()) || {}}
          />
        }
        menuItems={[
          {
            Icon: AddIcon,
            actions: [
              { title: 'New Freight Order', onClick: () => setCreateOpen(true) },
              { title: 'Create Shipment from Order', onClick: () => setShipmentCreateOpen(true) },
            ],
          },
        ]}
        tabs={['Inventory', 'Attachments', 'Events']}
      >
        <InventoryTab order={order} />
        <AttachmentsTab />
        <EventsTab />
      </DetailPage>
    </>
  )
}
